'use client'

import Image from 'next/image'
import { useTranslations } from 'next-intl'
import { useOpenStatusLabel } from './OpenStatusBadge'
import type { OpenStatus } from '@/lib/hours/openStatus'

type Props = {
  businessName: string
  tagline?: string
  heroImageUrl?: string
  heroVideoUrl?: string
  city?: string | null
  pickupWaitMinutes?: number
  readyInLabel?: string
  onViewMenu: () => void
  onDelivery?: () => void
  hasDelivery?: boolean
  openStatus?: OpenStatus | null
}

/** Colores de firma del tema deco: oro sobre noir del CTA y esmeralda. */
const GOLD_ON_NOIR = '#e9c87a'
const EMERALD = '#123f33'

/** Rombo ornamental — firma deco. */
function Diamond() {
  return (
    <span
      className="inline-block w-[5px] h-[5px] rotate-45"
      style={{ background: 'var(--ms-accent)' }}
      aria-hidden
    />
  )
}

/**
 * Hero del tema "deco" (glamour geométrico): programa de gala art déco.
 * Composición CENTRADA y simétrica, nombre en marco de doble línea (tinta +
 * latón con offset), rombos flanqueando el tagline, caps con tracking ancho y
 * esquinas rectas en todo. La foto va en panel panorámico con doble marco.
 * Poiret One solo en tamaños grandes (es thin); las caps chicas van en el
 * fontBody. La barra superior usa modo sólido (navSolid). Lee tokens --ms-*.
 */
export function HeroDeco({
  businessName,
  tagline,
  heroImageUrl,
  heroVideoUrl,
  city,
  pickupWaitMinutes,
  readyInLabel,
  onViewMenu,
  onDelivery,
  hasDelivery,
  openStatus,
}: Props) {
  const t = useTranslations('public.microsite')
  const statusLabel = useOpenStatusLabel(openStatus ?? null)
  const hasMedia = !!(heroImageUrl || heroVideoUrl)

  return (
    <section className="w-full" style={{ background: 'var(--ms-bg)' }}>
      {/* pt despeja la barra superior fija (navSolid) */}
      <div className="mx-auto max-w-4xl px-6 sm:px-8 pt-24 sm:pt-28 pb-10 text-center">
        {city && (
          <div
            className="text-[11px] font-bold uppercase tracking-[0.3em] pl-[0.3em] mb-7"
            style={{ color: 'var(--ms-accent)' }}
          >
            {city}
          </div>
        )}

        {/* Nombre en marco de doble línea: tinta + latón con offset */}
        <div
          className="inline-block px-8 sm:px-12 py-5 sm:py-6"
          style={{
            border: '1px solid var(--ms-text)',
            outline: '1px solid var(--ms-accent)',
            outlineOffset: '4px',
          }}
        >
          <h1
            className="uppercase leading-[1.08] tracking-[0.14em] pl-[0.14em] text-[clamp(30px,5.5vw,54px)]"
            style={{ fontFamily: 'var(--ms-font-display)', color: 'var(--ms-text)' }}
          >
            {businessName}
          </h1>
        </div>

        {tagline && (
          <div className="mt-6 flex items-center justify-center gap-3">
            <Diamond />
            <p
              className="text-[12px] font-semibold uppercase tracking-[0.22em] pl-[0.22em] max-w-xl"
              style={{ color: 'var(--ms-text-soft)' }}
            >
              {tagline}
            </p>
            <Diamond />
          </div>
        )}

        {/* Meta discreta */}
        <div
          className="mt-6 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[12.5px] tracking-wide"
          style={{ color: 'var(--ms-text-muted)' }}
        >
          {statusLabel && (
            <span className="inline-flex items-center gap-1.5">
              <span
                className="w-[6px] h-[6px] rotate-45"
                style={{
                  background:
                    statusLabel.tone === 'open'
                      ? '#2e7d5b'
                      : statusLabel.tone === 'soon'
                        ? '#f59e0b'
                        : 'var(--ms-text-muted)',
                }}
              />
              {statusLabel.text}
            </span>
          )}
          {statusLabel && <span aria-hidden>·</span>}
          <span>{t('pickupAvailable')}</span>
          {pickupWaitMinutes ? (
            <>
              <span aria-hidden>·</span>
              <span>{readyInLabel ?? t('readyIn', { minutes: pickupWaitMinutes })}</span>
            </>
          ) : null}
        </div>

        {/* CTAs rectangulares: oro sobre noir + outline esmeralda */}
        <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
          <button
            type="button"
            onClick={onViewMenu}
            className="inline-flex items-center px-9 py-4 text-[12.5px] font-bold uppercase tracking-[0.18em] transition hover:brightness-110 active:scale-[0.985]"
            style={{ background: 'var(--ms-text)', color: GOLD_ON_NOIR }}
          >
            {t('orderOnline')}
          </button>
          {hasDelivery && onDelivery && (
            <button
              type="button"
              onClick={onDelivery}
              className="inline-flex items-center px-7 py-4 text-[12.5px] font-bold uppercase tracking-[0.18em] transition hover:bg-black/5"
              style={{ color: EMERALD, border: '1px solid var(--ms-accent)' }}
            >
              {t('delivery')}
            </button>
          )}
        </div>
      </div>

      {/* Foto en panel panorámico con doble marco (latón afuera, tinta adentro) */}
      {hasMedia && (
        <div className="mx-auto max-w-5xl px-6 sm:px-8 pb-16">
          <div className="p-1.5" style={{ border: '1px solid var(--ms-accent)' }}>
            <div
              className="relative w-full overflow-hidden"
              style={{ aspectRatio: '16 / 7', border: '1px solid var(--ms-text)' }}
            >
              {heroImageUrl ? (
                <Image src={heroImageUrl} alt={businessName} fill priority sizes="100vw" className="object-cover" />
              ) : heroVideoUrl ? (
                <video autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" src={heroVideoUrl} />
              ) : null}
            </div>
          </div>
        </div>
      )}
    </section>
  )
}
